import { useMemo, useState } from "react";
import {
  Box, HStack, Icon, Link, Tooltip, VStack, Button, Spinner, useDisclosure,
} from "@chakra-ui/react";
import CDS from "design-system";
import { Link as RouterLink } from "react-router";
import {
  LuTriangleAlert, LuUserPlus, LuListChecks, LuClock, LuCalendar, LuCircleCheck,
} from "react-icons/lu";
import { format } from "date-fns";
import SideDrawer from "components/SideDrawer";
import ProfileAvatar from "components/ProfileAvatar";
import { NewHireJourney } from "models/v2/newHireJourney";
import { BasicUser } from "features/User/models";
import { Task } from "features/Resources/Task/models";
import { IssueSummary } from "models/issue";
import { Meeting } from "features/Resources/Meeting/models";
import { newHireJourneyPath, newHireJourneyPathById } from "helpers/url/pages/v1";
import { SHARED_ROUTES } from "definitions/constants/routeConstants";
import { useNewHireJourneyTasksQuery } from "features/Resources/Task/hooks";
import { useIssuesByNewHireIdQuery } from "features/Issues/hooks";
import { useNewHireJourneyMeetingsQuery } from "features/Resources/Meeting/hooks";
import { useCurrentUserQuery } from "features/User/hooks";
import { OPEN_TASK_STATUSES } from "features/Resources/Task/constants";
import { dayAndPeriodLabel } from "helpers/time";
import { useReminderRows } from "features/SendMessage/hooks";
import { ReminderRow } from "features/SendMessage/types";
import ReminderComposerModal from "features/SendMessage/Components/ReminderComposerModal";
import IssuesDrawer from "features/Issues/Components/IssuesDrawer";
import EditSupportCrewMemberButton, { BUDDY_FIELD_SCHEMA } from "features/NewHireJourney/Components/EditSupportCrewMemberButton";
import SkipRoleModal from "features/NewHireJourney/Components/SkipRoleModal";

interface JourneyActionItemsDrawerProps {
  journey: NewHireJourney | undefined;
  isOpen: boolean;
  onClose: () => void;
}

function StakeholderChip({ label, user }: { label: string; user: BasicUser | null | undefined }) {
  return (
    <Tooltip label={user?.fullName ?? `No ${label} assigned`} placement="top" hasArrow>
      <HStack spacing={1.5} align="center" cursor="default" minW={0}>
        <ProfileAvatar user={user ?? undefined} size="xs" />
        <VStack align="start" spacing={0} minW={0}>
          <CDS.Text10 color="fg.subtle" whiteSpace="nowrap">{label}</CDS.Text10>
          <CDS.Text10
            fontWeight="medium"
            color={user ? "gray.800" : "fg.subtle"}
            noOfLines={1}
            maxW="72px"
          >
            {user?.firstName ?? user?.fullName ?? "—"}
          </CDS.Text10>
        </VStack>
      </HStack>
    </Tooltip>
  );
}

function DrawerSection({
  icon, title, count, action, children,
}: {
  icon: React.ElementType;
  title: string;
  count?: number;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Box borderWidth="1px" borderColor="gray.200" borderRadius="lg" p={4}>
      <HStack spacing={2} mb={3} align="center" justify="space-between">
        <HStack spacing={2} align="center" minW={0}>
          <Icon as={icon} boxSize={4} color="gray.600" />
          <CDS.Text12 fontWeight="semibold" color="gray.900">{title}</CDS.Text12>
          {typeof count === "number" && count > 0 && (
            <CDS.Text10 color="fg.subtle">({count})</CDS.Text10>
          )}
        </HStack>
        {action}
      </HStack>
      {children}
    </Box>
  );
}

function BuddySection({
  journey, tasks, rows, onRemind, skipped, onSkipped,
}: {
  journey: NewHireJourney;
  tasks: Task[];
  rows: ReminderRow[];
  onRemind: (row: ReminderRow) => void;
  skipped: boolean;
  onSkipped: () => void;
}) {
  const skipModal = useDisclosure();
  if (journey.buddy || skipped) return null;
  const assignBuddyTask = tasks.find((task) => task.taskAction === "assign_buddy" && !!task.assignee);
  const assigneeRow = assignBuddyTask
    ? rows.find((row) => row.user.id === assignBuddyTask.assigneeId)
    : undefined;
  return (
    <DrawerSection icon={LuUserPlus} title="No buddy assigned">
      <HStack spacing={3} justify="space-between" align="center">
        <CDS.Text12 color="fg.subtle">Pair {journey.newHire?.firstName ?? "this new hire"} with a buddy.</CDS.Text12>
        <HStack spacing={2} flexShrink={0}>
          {assigneeRow && assignBuddyTask?.assignee && (
            <Button
              size="xs"
              variant="outline"
              onClick={() => onRemind(assigneeRow)}
            >
              Remind {assignBuddyTask.assignee.firstName ?? "to assign"}
            </Button>
          )}
          <Button size="xs" variant="ghost" color="fg.subtle" onClick={skipModal.onOpen}>
            Skip buddy
          </Button>
          <EditSupportCrewMemberButton
            newHireJourneyId={journey.id}
            formSchema={BUDDY_FIELD_SCHEMA}
            label="Buddy"
            renderTrigger={(onOpen) => (
              <Button size="xs" variant="outline" onClick={onOpen}>
                Assign buddy
              </Button>
            )}
          />
        </HStack>
      </HStack>
      <SkipRoleModal
        isOpen={skipModal.isOpen}
        onClose={skipModal.onClose}
        journeyId={journey.id}
        roleKey="buddy"
        roleLabel="buddy"
        newHireName={journey.newHire?.firstName ?? journey.newHire?.fullName ?? "this new hire"}
        onSkipped={onSkipped}
      />
    </DrawerSection>
  );
}

function MyTasksSection({ tasks, journey }: { tasks: Task[]; journey: NewHireJourney }) {
  if (tasks.length === 0) return null;
  return (
    <DrawerSection
      icon={LuListChecks}
      title="Your open tasks"
      count={tasks.length}
      action={(
        <Link
          as={RouterLink}
          to={`${SHARED_ROUTES.myTasks}?newHireId=${journey.id}`}
          fontSize="xs"
          fontWeight="medium"
          color="blue.600"
          whiteSpace="nowrap"
          _hover={{ textDecoration: "underline" }}
        >
          View in My Tasks →
        </Link>
      )}
    >
      <VStack align="stretch" spacing={2}>
        {tasks.map((task) => (
          <CDS.Text12 key={task.id} color="gray.900" noOfLines={1}>{task.name}</CDS.Text12>
        ))}
      </VStack>
    </DrawerSection>
  );
}

function WaitingOnOthersSection({
  rows, journey, onRemind,
}: {
  rows: ReminderRow[];
  journey: NewHireJourney;
  onRemind: (row: ReminderRow) => void;
}) {
  if (rows.length === 0) return null;
  return (
    <DrawerSection
      icon={LuClock}
      title="Tasks Not Completed by Others"
      count={rows.length}
      action={(
        <Link
          as={RouterLink}
          to={newHireJourneyPathById(journey.id, "tasks")}
          fontSize="xs"
          fontWeight="medium"
          color="blue.600"
          whiteSpace="nowrap"
          _hover={{ textDecoration: "underline" }}
        >
          View all open tasks →
        </Link>
      )}
    >
      <VStack align="stretch" spacing={3}>
        {rows.map((row) => (
          <HStack key={row.user.id} spacing={3} justify="space-between" align="center">
            <HStack spacing={2} minW={0}>
              <ProfileAvatar user={row.user} size="xs" />
              <VStack align="start" spacing={0} minW={0}>
                <CDS.Text12 fontWeight="medium" color="gray.900" noOfLines={1}>
                  {row.user.fullName}
                </CDS.Text12>
                <CDS.Text10 color="fg.subtle">
                  {row.tasks.length} open task{row.tasks.length === 1 ? "" : "s"}
                  {row.recency === "recent" && " · recently reminded"}
                </CDS.Text10>
              </VStack>
            </HStack>
            <Button
              size="xs"
              variant="outline"
              colorScheme={row.recency === "recent" ? "gray" : undefined}
              color={row.recency === "recent" ? "fg.muted" : undefined}
              onClick={() => onRemind(row)}
            >
              Remind
            </Button>
          </HStack>
        ))}
      </VStack>
    </DrawerSection>
  );
}

function IssuesSection({ issues, onViewAll }: { issues: IssueSummary[]; onViewAll: () => void }) {
  if (issues.length === 0) return null;
  return (
    <DrawerSection
      icon={LuTriangleAlert}
      title="Issues"
      count={issues.length}
      action={(
        <Link
          as="button"
          onClick={onViewAll}
          fontSize="xs"
          fontWeight="medium"
          color="blue.600"
          whiteSpace="nowrap"
          _hover={{ textDecoration: "underline" }}
        >
          View all →
        </Link>
      )}
    >
      <VStack align="stretch" spacing={2}>
        {issues.map((issue) => (
          <CDS.Text12 key={issue.id} color="gray.900" noOfLines={1}>
            {issue.issueType.replaceAll("_", " ")}
          </CDS.Text12>
        ))}
      </VStack>
    </DrawerSection>
  );
}

function UnscheduledMeetingsSection({
  meetings, journey,
}: {
  meetings: Meeting[];
  journey: NewHireJourney;
}) {
  if (meetings.length === 0) return null;
  return (
    <DrawerSection
      icon={LuCalendar}
      title="Unscheduled meetings"
      count={meetings.length}
      action={(
        <Link
          as={RouterLink}
          to={newHireJourneyPathById(journey.id, "meetings")}
          fontSize="xs"
          fontWeight="medium"
          color="blue.600"
          whiteSpace="nowrap"
          _hover={{ textDecoration: "underline" }}
        >
          Schedule meetings here →
        </Link>
      )}
    >
      <VStack align="stretch" spacing={2}>
        {meetings.map((meeting) => (
          <CDS.Text12 key={meeting.id} color="gray.900" noOfLines={1}>{meeting.name}</CDS.Text12>
        ))}
      </VStack>
    </DrawerSection>
  );
}

function AllClearState() {
  return (
    <VStack spacing={3} py={8} color="fg.subtle">
      <Icon as={LuCircleCheck} boxSize={10} color="green.500" />
      <CDS.Text12>Nothing needs attention right now.</CDS.Text12>
    </VStack>
  );
}

export default function JourneyActionItemsDrawer({ journey, isOpen, onClose }: JourneyActionItemsDrawerProps) {
  const journeyId = journey?.id;
  const enabled = isOpen && !!journeyId;
  const { data: currentUser } = useCurrentUserQuery();
  const { data: tasks = [], isLoading: tasksLoading } = useNewHireJourneyTasksQuery(journeyId ?? "", { enabled });
  const { data: issues = [], isLoading: issuesLoading } = useIssuesByNewHireIdQuery(journeyId ?? "", { enabled });
  const { data: meetings = [], isLoading: meetingsLoading } = useNewHireJourneyMeetingsQuery(journeyId ?? "", { enabled });

  const [reminderRow, setReminderRow] = useState<ReminderRow | null>(null);
  const [locallySkippedRoles, setLocallySkippedRoles] = useState<string[]>([]);
  const issuesDrawer = useDisclosure();

  const { rows: otherRows } = useReminderRows(journey as NewHireJourney, {
    currentUserId: currentUser?.id,
    enabled: enabled && !!journey,
  });

  const openTasks = useMemo(
    () => tasks.filter((task) => OPEN_TASK_STATUSES.includes(task.status)),
    [tasks],
  );

  const myOpenTasks = useMemo(
    () => (currentUser ? openTasks.filter((task) => task.assigneeId === currentUser.id) : []),
    [openTasks, currentUser],
  );

  const unscheduledMeetings = useMemo(
    () => meetings.filter((meeting) => meeting.status === "pending_scheduling"),
    [meetings],
  );

  const isLoading = tasksLoading || issuesLoading || meetingsLoading;
  const buddySkipped = !!journey?.skippedRoles?.includes("buddy") || locallySkippedRoles.includes("buddy");
  const buddyMissing = !!journey && !journey.buddy && !buddySkipped;
  const nothingToShow = !buddyMissing
    && myOpenTasks.length === 0
    && otherRows.length === 0
    && issues.length === 0
    && unscheduledMeetings.length === 0;

  return (
    <>
      <SideDrawer<NewHireJourney>
        isOpen={isOpen}
        onClose={onClose}
        item={journey}
        size="md"
        hideCloseButton
        header={({ item }) => (
          <Box
            as={RouterLink}
            to={item ? newHireJourneyPath(item) : "#"}
            borderWidth="1px"
            borderColor="gray.200"
            borderRadius="xl"
            px={4}
            py={3}
            display="block"
            w="100%"
            _hover={{ bg: "gray.50", textDecoration: "none" }}
          >
            <VStack align="stretch" spacing={3}>
              <HStack spacing={3} align="center" justify="space-between">
                <HStack spacing={3} align="center" minW={0} flex="1">
                  <ProfileAvatar user={item?.newHire} size="sm" />
                  <VStack align="start" spacing={0.5} minW={0}>
                    <CDS.Text14 fontWeight="bold" noOfLines={1}>
                      {item?.newHire?.fullName ?? "Journey"}
                    </CDS.Text14>
                    {item?.newHire?.title && (
                      <CDS.Text12 color="gray.600" noOfLines={1}>{item.newHire.title}</CDS.Text12>
                    )}
                    {item?.startDate && (
                      <CDS.Text10 color="fg.subtle">
                        {[
                          dayAndPeriodLabel(item.startDate),
                          `Started ${format(new Date(item.startDate), "MMM d, yyyy")}`,
                        ].filter(Boolean).join(" · ")}
                      </CDS.Text10>
                    )}
                  </VStack>
                </HStack>
                <CDS.Text10 color="fg.subtle" flexShrink={0}>Onboarding Journey →</CDS.Text10>
              </HStack>
              {item && (
                <HStack spacing={4}>
                  <StakeholderChip label="Manager" user={item.manager} />
                  <StakeholderChip label="Buddy" user={item.buddy} />
                  <StakeholderChip label="People Team" user={item.peopleTeamContact} />
                </HStack>
              )}
            </VStack>
          </Box>
        )}
      >
        {() => {
          if (!journey) return null;

          return (
            <VStack align="stretch" spacing={4} py={4}>
              {isLoading ? (
                <VStack py={4}>
                  <Spinner size="sm" />
                </VStack>
              ) : nothingToShow ? (
                <AllClearState />
              ) : (
                <>
                  <CDS.Text14 fontWeight="semibold" color="gray.700">Action items</CDS.Text14>
                  <BuddySection
                    journey={journey}
                    tasks={openTasks}
                    rows={otherRows}
                    onRemind={setReminderRow}
                    skipped={buddySkipped}
                    onSkipped={() => setLocallySkippedRoles((prev) => (prev.includes("buddy") ? prev : [...prev, "buddy"]))}
                  />
                  <MyTasksSection tasks={myOpenTasks} journey={journey} />
                  <WaitingOnOthersSection rows={otherRows} journey={journey} onRemind={setReminderRow} />
                  <IssuesSection issues={issues} onViewAll={issuesDrawer.onOpen} />
                  <UnscheduledMeetingsSection meetings={unscheduledMeetings} journey={journey} />
                </>
              )}
            </VStack>
          );
        }}
      </SideDrawer>
      {reminderRow && journey && (
        <ReminderComposerModal
          isOpen
          onClose={() => setReminderRow(null)}
          row={reminderRow}
          newHireFullName={journey.newHire?.fullName}
          newHireId={journey.newHire?.id}
        />
      )}
      {journey && (
        <IssuesDrawer
          newHireId={journey.id}
          isOpen={issuesDrawer.isOpen}
          onClose={issuesDrawer.onClose}
        />
      )}
    </>
  );
}
